import { useState, useEffect } from 'react';
import jwt from 'jsonwebtoken';
import * as service from '../../../services/Auth';
import { CodeConstant, MessageConstant, StatusConstant } from '../../../../constants';
import { setIdentity, pushLog2DV2LogServer } from '../../../services/logging';
import { logLoadTime } from '../../../services/util/PageLoad';
import {  getUserEntranceInformation } from '../../../services/Common';
import { checkVetsmithSubscribe } from '../../../legacy_components/UI_V2_Components/Vetsmith/VetsmithSubscribe';
import fullStoryIdentify from '../../../../services/fullstory';
import clarityIdentify from '../../../../services/clarity';
import { turingLogger } from '../../../shared/TuringLogger';

export function useLogin(authenticate, tokenExpired, redirect=true) {
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [agentDataInfo, setAgentDataInfo] = useState(null);
  const [entranceData, setEntranceData] = useState(null);

  useEffect(() => {
    const agentData = localStorage.getItem('agentDataInfo');
    const authenticated = localStorage.getItem('authenticated');
    if (agentData) setAgentDataInfo(JSON.parse(agentData));
    setEntranceData(getUserEntranceInformation('login'));
    if (!authenticated) {
      localStorage.clear();
    }
  }, []);

  const handleSubmit = async (values, callback) => {
    if (errorMessage) return;
    setLoading(true);
    tokenExpired(false);
    const params = {
      email: values.email,
      password: values.password,
      fromWhere: StatusConstant.LOGIN_EMAIL,
      entranceData,
      ...agentDataInfo,
    };

    try {
      const res = await service.login(params);
      if (res.data.code === CodeConstant.OK || res.data.code === CodeConstant.CREATED) {
        localStorage.setItem('token', res.data.data.token);
        localStorage.setItem('refresh_token', res.data.data.refreshToken);
        localStorage.setItem('authenticated', 'true');
        fullStoryIdentify();
        clarityIdentify();
        authenticate({ authenticated: true });
        logLoadTime();
        const logData = { email: values.email, user_id: jwt.decode(res.data.data.token).uid };
        setIdentity(logData);
        pushLog2DV2LogServer.info('USER_LOGGING', 'LOGIN_SUCCESS', logData, params, null);
        turingLogger.log({ eventName: 'SIGNIN', logData: { ...logData, authMethod: 'EMAIL' } });
        if (await checkVetsmithSubscribe()) return;
        if (redirect) service.redirectUser(res.data.data.url);
        if (callback && typeof callback === 'function') callback({ accountExists: true, email: values.email })
      } else {
        setErrorMessage(res.data.message);
      }
    } catch (error) {
      setErrorMessage(MessageConstant.NETWORK_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (data, callback) => {
    setLoading(true);
    tokenExpired(false);
    const loginData = {
      googleId: data.googleId,
      fromWhere: StatusConstant.LOGIN_GOOGLE,
      entranceData,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      accessToken: data.accessToken,
    };

    try {
      const res = await service.loginWithGoogle(loginData);
      if (res.data.code === CodeConstant.OK || res.data.code === CodeConstant.CREATED) {
        localStorage.setItem('token', res.data.data.token);
        localStorage.setItem('refresh_token', res.data.data.refreshToken);
        localStorage.setItem('authenticated', 'true');
        fullStoryIdentify();
        clarityIdentify();
        authenticate({ authenticated: true });
        logLoadTime();
        const logData = { email: data.email, user_id: jwt.decode(res.data.data.token).uid };
        setIdentity(logData);
        pushLog2DV2LogServer.info('USER_LOGGING', 'SIGN_IN_GOOGLE', logData, loginData, null);
        turingLogger.log({ eventName: 'SIGNIN', logData: { ...logData, authMethod: 'GOOGLE' } }).catch(e => console.error(e));
        const { accountExists } = res.data;
        if (await checkVetsmithSubscribe()) return;
        if (redirect) service.redirectUser(res.data.data.url);
        if (callback && typeof callback === 'function') callback({ email: data.email, accountExists })
      } else {
        setErrorMessage(res.data.message);
      }
    } catch (error) {
      setErrorMessage(MessageConstant.NETWORK_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const resetError = () => setErrorMessage('');

  return {
    handleSubmit,
    handleSocialLogin,
    resetError,
    loading,
    errorMessage,
  };
}
